import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Storage } from '@angular/fire/storage';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { EmptyStateComponent } from '@principle-theorem/ng-principle-shared';
import { getDownloadURL$, NgDialogModule } from '@principle-theorem/ng-shared';
import {
  ACC_DOCUMENT_CONTENT_TYPE_OPTIONS,
  IAccClaimDocument,
  IAccClaimFundingRequest,
} from '@principle-theorem/principle-core/interfaces';
import { multiSwitchMap, WithRef } from '@principle-theorem/shared';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

interface IAccDocumentView {
  url: SafeResourceUrl;
  label: string;
}

export interface IAccViewClaimDocumentsDialogData {
  claimRequest: WithRef<IAccClaimFundingRequest>;
}

@Component({
  selector: 'pr-acc-view-claim-documents-dialog',
  imports: [NgDialogModule, EmptyStateComponent, MatTabsModule],
  templateUrl: './acc-view-claim-documents-dialog.component.html',
  styleUrl: './acc-view-claim-documents-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccViewClaimDocumentsDialogComponent {
  private _storage = inject(Storage);
  private _sanitiser = inject(DomSanitizer);
  data = inject<IAccViewClaimDocumentsDialogData>(MAT_DIALOG_DATA);
  documents$: Observable<IAccDocumentView[]>;

  constructor() {
    const claimDocuments$ = of(this.data.claimRequest.data.documents ?? []);

    this.documents$ = claimDocuments$.pipe(
      multiSwitchMap((document) =>
        getDownloadURL$(this._storage, document.path).pipe(
          map((url) => ({
            label: this._getPdfLabel(document),
            url: this._sanitiser.bypassSecurityTrustResourceUrl(url),
          }))
        )
      )
    );
  }

  private _getPdfLabel(document: IAccClaimDocument): string {
    const contentType = ACC_DOCUMENT_CONTENT_TYPE_OPTIONS.find(
      (option) => option.value === document.metadata.contentType
    );
    return contentType?.label ?? 'Unknown Document Type';
  }
}
